/**
 * Helpers for matching furniture dimensions against catalogue model-number
 * dimension specs and for locating model numbers within the nested
 * `useGetModelNumbers` response shape.
 *
 * Used by the "Select model number" modal in the Furniture Sidebar.
 */

import type {
	TCategoriesWithModels,
	TCategoryWithModels,
	TDimensions,
	TModelNumber,
} from "types/modelNumbers.type"

/** Outcome of comparing furniture dimensions to a model's allowed dimensions. */
export type TDimensionTag = "best" | "partial" | "none"

/** Per-axis in-range flags plus the aggregate dimension tag for a single model number. */
export type TMatchResult = {
	inRange: { width: boolean; height: boolean; depth: boolean }
	tag: TDimensionTag
	outOfRangeCount: number
}

/**
 * Tests whether a furniture dimension `value` (inches) satisfies a single
 * catalogue dimension entry. An entry is either:
 *  - an exact value, e.g. `"24"` — matches when `value === 24`
 *  - an inclusive range, e.g. `"20-30"` — matches when `20 <= value <= 30`
 *
 * Whitespace around the entry is tolerated. Min/max in a range may be in
 * either order. Returns `false` for empty / non-numeric / malformed entries.
 */
export const dimensionEntryMatches = (entry: string, value: number): boolean => {
	const trimmed = entry.trim()
	if (trimmed === "") return false
	const rangeMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/)
	if (rangeMatch) {
		const min = Number(rangeMatch[1])
		const max = Number(rangeMatch[2])
		if (Number.isNaN(min) || Number.isNaN(max)) return false
		const lo = Math.min(min, max)
		const hi = Math.max(min, max)
		return value >= lo && value <= hi
	}
	const exact = Number(trimmed)
	if (Number.isNaN(exact)) return false
	return value === exact
}

/**
 * Returns `true` when `value` satisfies *any* of the catalogue's dimension
 * `entries` for one axis (each axis on a model number is an array of allowed
 * values/ranges). A `null` or non-finite `value` always returns `false` —
 * a furniture without that dimension set cannot be considered "in range".
 */
export const dimensionMatches = (entries: string[] | undefined, value: number | null): boolean => {
	if (value === null || !Number.isFinite(value)) return false
	if (!entries || entries.length === 0) return false
	return entries.some((entry) => dimensionEntryMatches(entry, value))
}

/**
 * Compares a furniture's W/H/D against a model number's allowed dimensions
 * and produces a `TMatchResult`:
 *  - 0 axes out of range → `tag: "best"`
 *  - 1 or 2 axes out of range → `tag: "partial"`
 *  - 3 axes out of range → `tag: "none"`
 *
 * A `null` furniture axis counts as out-of-range for that axis.
 */
export const computeMatch = (
	modelDims: TDimensions,
	furniture: { width: number | null; height: number | null; depth: number | null },
): TMatchResult => {
	const inRange = {
		width: dimensionMatches(modelDims.width, furniture.width),
		height: dimensionMatches(modelDims.height, furniture.height),
		depth: dimensionMatches(modelDims.depth, furniture.depth),
	}
	const outOfRangeCount = Number(!inRange.width) + Number(!inRange.height) + Number(!inRange.depth)
	let tag: TDimensionTag = "best"
	if (outOfRangeCount === 3) tag = "none"
	else if (outOfRangeCount > 0) tag = "partial"
	return { inRange, tag, outOfRangeCount }
}

/**
 * `computeMatch` against SEVERAL furniture at once, for the batch model-number picker (REQ-403).
 *
 * A batch selection has no single W/H/D to score against, so an axis counts as in range only when
 * EVERY target's value for it is in range — the model number has to fit the whole selection, not
 * one arbitrary member of it. The tag is then derived from the aggregated axes exactly as
 * `computeMatch` does, which is what keeps the row tags (`Best match` / `Partial match` /
 * `No match`) and the red out-of-range values reading the same in both panels.
 *
 * An empty target list degenerates to `computeMatch` with no dimensions set: every axis is out of
 * range, so the row is tagged `none`.
 */
export const computeMatchForMany = (
	modelDims: TDimensions,
	targets: readonly { width: number | null; height: number | null; depth: number | null }[],
): TMatchResult => {
	if (targets.length === 0) return computeMatch(modelDims, { width: null, height: null, depth: null })
	const inRange = { width: true, height: true, depth: true }
	for (const target of targets) {
		const single = computeMatch(modelDims, target)
		inRange.width = inRange.width && single.inRange.width
		inRange.height = inRange.height && single.inRange.height
		inRange.depth = inRange.depth && single.inRange.depth
	}
	const outOfRangeCount = Number(!inRange.width) + Number(!inRange.height) + Number(!inRange.depth)
	let tag: TDimensionTag = "best"
	if (outOfRangeCount === 3) tag = "none"
	else if (outOfRangeCount > 0) tag = "partial"
	return { inRange, tag, outOfRangeCount }
}

/** Flattens every category node across the `/categories/` response. */
const collectAllCategoryNodes = (data: TCategoriesWithModels | undefined): TCategoryWithModels[] => {
	if (!data) return []
	const out: TCategoryWithModels[] = []
	for (const top of Object.values(data)) {
		for (const list of Object.values(top)) {
			if (Array.isArray(list)) out.push(...list)
		}
	}
	return out
}

/**
 * Builds a `TModelNumber[]` from a single category node returned by
 * `/categories/`. Combines `front_model_numbers`, `model_number_to_id`, and
 * `frontend_model_numbers_to_dimensions` into the shape the modal renders.
 *
 * A row needs BOTH an id and a dimensions entry. Measured against the live
 * `/categories/` payload, no category loses a single model number to a missing
 * dimensions entry (0 of 386 nodes), so relaxing this would not add rows — what
 * DOES happen is that 120 of the 222 displayed categories carry no model numbers
 * at all, i.e. the category genuinely has none to offer. That case is the modal's
 * empty state to explain, not something this function can fix.
 */
const buildModelsFromCategoryNode = (node: TCategoryWithModels): TModelNumber[] => {
	const dims = node.frontend_model_numbers_to_dimensions ?? {}
	const idMap = node.model_number_to_id ?? {}
	return (node.front_model_numbers ?? [])
		.map((modelNumber) => {
			const id = idMap[modelNumber]
			const d = dims[modelNumber]
			if (id === undefined || !d) return null
			const model: TModelNumber = {
				id,
				model_number: modelNumber,
				image_from_stevens_catalogue: null,
				category_hierarchy: {
					"Top Level Category": { id: 0, name: node.top_level_category },
					"Sub Category": { id: 0, name: node.supercategory },
					"Model Category": { id: node.id, name: node.name },
				},
				dimensions: d,
			}
			return model
		})
		.filter((m): m is TModelNumber => m !== null)
}

/**
 * Two `/categories/` nodes render as the SAME category everywhere the user can
 * see one — the sidebar's Object select, the furniture header, this modal's tab —
 * when all three levels of their path match. Names never repeat across
 * super-categories in the live payload, so the triple is unambiguous.
 */
const isSameDisplayedCategory = (a: TCategoryWithModels, b: TCategoryWithModels): boolean =>
	a.name === b.name && a.supercategory === b.supercategory && a.top_level_category === b.top_level_category

/**
 * Returns the model numbers of the category the user SEES — which is not the same
 * thing as the model numbers of one `/categories/` node.
 *
 * `/categories/` splits a single displayed category into one node per structural
 * variant, each with its own id and typically ONE model number: column count
 * (`type: "column_based_object"`, e.g. `lc:lcabcord4sc[r]_1` … `_4`), composite
 * index, or shelf count. The furniture stores exactly one of those ids, so keying
 * off it alone renders a one-row list and hides that variant's peers — 233 of the
 * 386 nodes in the live payload sit in such a group.
 *
 * Grouping by the displayed path makes this list agree with `GET /model-numbers/`,
 * which already collapses them: the four ids above come back from it nested under
 * the single key `locker - corridor closed door[r] - 4 split compartment`. It also
 * equals the backend's own `related_column_based_categories` wherever that field
 * exists (0 mismatches across the payload) while additionally covering the
 * composite and shelves groups, which carry no such field.
 *
 * Each row keeps the id of the node it came from in
 * `category_hierarchy["Model Category"].id`. A peer's model number belongs to a
 * DIFFERENT category id, so selecting one is a category move (invisible to the
 * user, since the name is identical) and must be saved through
 * `select-arbitrary-model-number/` — the plain furniture PATCH rejects it.
 */
export const findModelNumbersForCategory = (
	data: TCategoriesWithModels | undefined,
	categoryId: number | undefined | null,
): TModelNumber[] => {
	if (!data || categoryId == null) return []
	const nodes = collectAllCategoryNodes(data)
	const node = nodes.find((n) => n.id === categoryId)
	if (!node) return []

	const seen = new Set<number>()
	const models: TModelNumber[] = []
	for (const sibling of nodes) {
		if (!isSameDisplayedCategory(sibling, node)) continue
		for (const model of buildModelsFromCategoryNode(sibling)) {
			if (seen.has(model.id)) continue
			seen.add(model.id)
			models.push(model)
		}
	}
	return models
}

/**
 * Returns every model number across every category node. Used when the
 * furniture is in the "Other" top-level category, which has no own id and
 * therefore no category-scoped model list — the user can pick any model.
 * Deduplicated by `id`.
 */
export const findAllModelNumbers = (data: TCategoriesWithModels | undefined): TModelNumber[] => {
	if (!data) return []
	const seen = new Set<number>()
	const all: TModelNumber[] = []
	for (const node of collectAllCategoryNodes(data)) {
		for (const model of buildModelsFromCategoryNode(node)) {
			if (seen.has(model.id)) continue
			seen.add(model.id)
			all.push(model)
		}
	}
	return all
}

/**
 * Normalizes a model number for comparison: trims whitespace, strips wrapping
 * single/double quotes, and lowercases.
 *
 * Why: the backend returns `furniture.detected_model_numbers` wrapped in extra
 * single quotes (e.g. `"'10129'"`), but the catalogue stores them unquoted
 * (`"10129"`). Normalizing both sides lets us compare them reliably.
 */
export const normalizeModelNumber = (value: string): string =>
	value.trim().replace(/^['"]+|['"]+$/g, "").trim().toLowerCase()

/**
 * Joins a single axis's catalogue dimension entries for display, e.g.
 * `["0", "999", "24-30"]` → `"0,999,24-30"`. Returns an em-dash for
 * empty/missing input.
 */
export const formatDimensionEntries = (entries: string[] | undefined): string => {
	if (!entries || entries.length === 0) return "—"
	return entries.join(",")
}
